import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Play, Lock } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useVideoMembershipAccess } from '@/hooks/useVideoMembershipAccess';
import { usePurchaseMembership } from '@/hooks/usePurchaseMembership';

const VideoMembershipPurchase: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasAccess, requiredMembership, loading } = useVideoMembershipAccess('video_hoops_recaps');
  const { purchaseMembership, isProcessing } = usePurchaseMembership();

  const formatPrice = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  };

  const handlePurchase = async () => {
    if (!requiredMembership) return;

    await purchaseMembership({
      membership_slug: requiredMembership.slug,
      billing_type: requiredMembership.billing_type,
      price_cents: requiredMembership.price_cents,
      stripe_price_id: requiredMembership.stripe_price_id,
      is_addon: true,
      success_url: `${window.location.origin}/videos/hoops_recaps/watch`,
      cancel_url: window.location.href
    });
  };

  if (!user) {
    navigate('/auth');
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Loading membership details...</p>
        </div>
      </div>
    );
  }

  if (hasAccess) {
    navigate('/videos/hoops_recaps/watch');
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-primary text-primary-foreground py-12">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl font-bold mb-4">Video Hoops Recaps</h1>
          <p className="text-lg opacity-90 max-w-2xl mx-auto">
            Get exclusive access to professional basketball game recaps and highlights
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <Card className="overflow-hidden border-primary">
            <CardHeader className="bg-primary/10">
              <div className="flex items-center justify-between">
                <CardTitle className="text-2xl">Premium Video Access</CardTitle>
                <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                  PREMIUM CONTENT
                </Badge>
              </div>
            </CardHeader>
            
            <CardContent className="p-6">
              <div className="text-center mb-6">
                <div className="text-4xl font-bold text-primary mb-2">
                  $29.99
                  <span className="text-lg font-normal text-muted-foreground">/month</span>
                </div>
                <p className="text-muted-foreground">
                  Monthly subscription with exclusive basketball content
                </p>
              </div>

              <div className="space-y-4 mb-8">
                <h4 className="font-semibold text-lg">What's Included:</h4>
                <div className="grid gap-3">
                  {[
                    'Full game recap videos',
                    'Key highlight reels',
                    'Expert game analysis',
                    'Player performance breakdowns',
                    'Exclusive behind-the-scenes content',
                    'Monthly tournaments coverage',
                    'Unlimited streaming access'
                  ].map((feature, index) => (
                    <div key={index} className="flex items-center">
                      <Check className="h-5 w-5 text-green-600 mr-3" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <Button 
                  onClick={handlePurchase}
                  disabled={isProcessing || !requiredMembership}
                  className="w-full h-12 text-lg"
                >
                  {isProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <Lock className="h-4 w-4 mr-2" />
                      Subscribe for $29.99/month
                    </>
                  )}
                </Button>

                <div className="text-center text-sm text-muted-foreground">
                  <p>Cancel anytime • Secure payment via Stripe</p>
                  <p className="mt-1">By subscribing, you agree to our Terms of Service</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="mt-6 text-center">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/videos/hoops_recaps')}
              className="text-muted-foreground hover:text-foreground"
            >
              ← Back to Video Preview
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoMembershipPurchase;